import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { authedFetch } from '../lib/api';
import { ErrorBanner } from './ErrorBanner';

interface Props {
  tenantCode: string;
  chatId: string;
  onSelect?: (subscriptionId: number) => void;
}

interface SubItem {
  id: number;
  status: string;
  total: number;
  currency: string;
  billing_period: string;
  billing_interval: number;
  start_date: string | null;
  next_payment: string | null;
  last_payment: string | null;
  parent_order_id: number;
  items: Array<{ name: string; quantity: number; total: number }>;
  edit_url: string;
}

interface SubsResponse {
  configured: boolean;
  gated?: string;
  reason?: string;
  items?: SubItem[];
  count?: number;
}

export function WpSubscriptionsPanel({ tenantCode, chatId, onSelect }: Props) {
  const { user } = useAuth();
  const [data, setData] = useState<SubsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const qs = `?tenantCode=${encodeURIComponent(tenantCode)}&chatId=${encodeURIComponent(chatId)}`;
        const res = await authedFetch<SubsResponse>(`/agent/wp/subscriptions${qs}`);
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantCode, chatId, user]);

  if (loading) return <div className="py-1 text-[11px] text-gray-400">Loading…</div>;
  if (error) return <ErrorBanner error={error} label="Error" className="py-1 text-[11px] text-red-600" />;
  if (!data || data.configured === false) return null;
  if (data.gated === 'not_verified') {
    return (
      <div className="rounded-md bg-amber-50 border border-amber-200 px-2.5 py-2 text-[11px] text-amber-700">
        Verify the customer (last-4 CC) to view subscriptions.
      </div>
    );
  }

  const items = data.items ?? [];
  const isActive = (s: SubItem) => s.status === 'active' || s.status === 'pending-cancel';
  const activeCount = items.filter(isActive).length;
  const inactiveCount = items.length - activeCount;
  const shown = showInactive ? items : items.filter(isActive);
  return (
    <div className="space-y-1.5">
      {inactiveCount > 0 && (
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-gray-500">
            {showInactive ? `All (${items.length})` : `${activeCount} active`}
          </span>
          <button
            type="button"
            onClick={() => setShowInactive((v) => !v)}
            className="text-[11px] font-medium text-blue-600 hover:underline"
          >
            {showInactive ? 'Active only' : `Show inactive (${inactiveCount})`}
          </button>
        </div>
      )}
      {shown.length === 0 && (
        <div className="py-1 text-[11px] text-gray-400">
          {items.length === 0 ? 'No subscriptions.' : 'No active subscriptions.'}
        </div>
      )}
      {shown.map((s) => (
        <div
          key={s.id}
          onClick={() => onSelect?.(s.id)}
          className="cursor-pointer rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 shadow-sm hover:bg-gray-50"
          title="Open debt ledger"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="text-[12px] font-semibold text-gray-900 truncate">
              {s.items.map((i) => i.name).join(', ') || `Subscription #${s.id}`}
            </span>
            <StatusPill status={s.status} />
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-gray-500">
            <span className="font-medium text-gray-700">{s.total.toFixed(2)} {s.currency}</span>
            <span>· {s.billing_interval} {s.billing_period}</span>
            {s.next_payment && <span>· next {fmtDate(s.next_payment)}</span>}
            <a
              href={s.edit_url}
              onClick={(e) => e.stopPropagation()}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto text-[10px] text-gray-400 hover:text-gray-600"
            >
              #{s.id} ↗
            </a>
          </div>
        </div>
      ))}
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  return (
    <span className={`text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full flex-shrink-0 ${statusColor(status)}`}>
      {status}
    </span>
  );
}

function statusColor(s: string): string {
  if (s === 'active') return 'bg-green-100 text-green-700';
  if (s === 'on-hold' || s === 'pending' || s === 'pending-cancel') return 'bg-amber-100 text-amber-700';
  if (s === 'cancelled' || s === 'expired') return 'bg-gray-200 text-gray-600';
  return 'bg-blue-100 text-blue-700';
}

function fmtDate(s: string | null): string {
  if (!s) return '';
  try {
    return s.slice(0, 10);
  } catch {
    return s;
  }
}
